import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CheckoutService } from '@df/checkout/checkout.service';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Nexus } from '@tomandco/nexus-api-types';
import { Subscription } from 'rxjs/internal/Subscription';
import { ToggleBlockAbstact } from '../../common/toggle-block.abstract';
import { ResultComponent } from '../../ui/api/result.component';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { RadioComponent } from '../../ui/form/radio/radio.component';
import { BasketFastCheckoutFormComponent } from './basket-fast-checkout-form.component';
import { BasketFastCheckoutStepComponent } from './basket-fast-checkout-step.component';

export interface IFastCheckoutShippingTrayDaya {
    method?: string;
    country?: string;
}

@Component({
    selector: 'fast-checkout-shipping-methods-tray',
    templateUrl: './fast-checkout-shipping-methods-tray.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        OffClickDirective,
        OrderByPipe,
        PriceComponent,
        RadioComponent,
        RadioGroupDirective,
        ResultComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FastCheckoutShippingMethodsTrayComponent extends ToggleBlockAbstact implements OnInit, OnDestroy {
    customRulesForDisabledDelivery: number[] = [139, 145];
    forbiddenCountries: string[] = ['GG', 'JE', 'IE', 'IM'];

    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    @Input()
    checkoutFormComponent!: BasketFastCheckoutFormComponent; // | MinibasketFastCheckoutFormComponent

    @Input()
    checkoutStepComponent!: BasketFastCheckoutStepComponent; // | MinibasketFastCheckoutStepComponent

    /**
     * Checkout changes subscription
     */
    protected checkoutSubscription?: Subscription;

    protected subscriptions = new Subscription();

    name = 'fastcheckoutshippingmethods';

    countries?: Nexus.Lib.General.Country[];

    protected checkout = inject(CheckoutService);

    override ngOnInit() {
        this.checkoutSubscription = this.checkoutFormComponent.subscribe((change: Checkout.IStateChange) => this.onCheckoutChange(change));
        this.subscriptions.add(this.checkoutFormComponent.saveShippingAddressStatus.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.checkoutFormComponent.saveShippingMethodStatus.subscribe(() => this.detectChanges()));

        this.app.apiConfigService.loadCountries().then(() => {
            this.countries = this.app.apiConfigService.countries;

            this.detectChanges();
        });

        super.ngOnInit();
    }

    protected onCheckoutChange(change: Checkout.IStateChange) {
        if (change?.full) {
            this.countries = this.app.apiConfigService.countries;
        }

        if (this.shippingMethod) {
            // if method is no longer allowed then unset it
            if (!this.allowedShippingMethods?.find(i => i.code === this.shippingMethod)) {
                this.shippingMethod = undefined;
            }
        }
        this.preselectShippingMethod();
        this.detectChanges();
    }

    preselectShippingMethod() {
        if (!this.shippingMethod && this.allowedShippingMethods?.length) {
            this.shippingMethod = this.allowedShippingMethods[0].code;
        }
    }

    get country(): string | undefined {
        return this.checkoutFormComponent.data?.address?.shipping?.country_id;
    }

    set country(input: string) {
        if (this.checkoutFormComponent.data.address.shipping.country_id !== input) {
            this.checkoutFormComponent.data.address.shipping.country_id = input;
            // remove default shipping address selection if country changed
            this.checkoutFormComponent.data.address.shipping.customer_address_id = 0;
            this.checkoutFormComponent.saveShippingAddress();
            this.detectChanges();
        }
    }

    get allowedShippingMethods(): Checkout.IShippingMethod[] | undefined {
        return this.checkoutFormComponent.nonCollectionShippingMethods;
    }

    get checkoutOnlyShippingMethods() {
        return this.checkoutFormComponent.collectionShippingMethods;
    }

    set shippingMethod(input: string | undefined) {
        this.checkoutFormComponent.data.shipping.method = input;
        this.checkoutFormComponent._saveShippingMethod();
        this.detectChanges();
    }

    get shippingMethod(): string | undefined {
        return this.checkoutFormComponent.data?.shipping?.method;
    }

    get saveShippingAddressStatus() {
        return this.checkoutFormComponent.saveShippingAddressStatus;
    }

    get saveShippingMethodStatus() {
        return this.checkoutFormComponent.saveShippingMethodStatus;
    }

    get showDeliveryError(): boolean {
        const customRules = this.app.customer.basket.data.custom_rules || [];
        if (this.country && this.forbiddenCountries.includes(this.country)) {
            return false;
        }
        return this.customRulesForDisabledDelivery.some(number => customRules.includes(number));
    }

    override ngOnDestroy() {
        this.checkoutSubscription?.unsubscribe();
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.subscriptions.unsubscribe();
        super.ngOnDestroy();
    }

    trackByCode(index: number, item: Nexus.Lib.General.Country): string {
        return item.code;
    }

    methodTrackFn(index, method: Checkout.IPaymentMethod) {
        return method.code;
    }
}
